// ACC-476: renders the ticketing admin settings inside Forecast's admin area at
// /admin/tickets. Mirrors ticketing-embed.tsx (ACC-434): supplies (a) Forecast's
// singleton Relay 13 environment via RelayProvider, (b) a session identity
// derived from the Forecast viewer via ticketing's AuthProvider so RequireAdmin /
// useAuth gate correctly, and (c) EmbeddedProvider(embedded=true) so the settings
// tabs render in embedded mode (Forecast supplies the surrounding chrome).
import React from 'react';
import {graphql} from 'react-relay';
import {RelayProvider} from './forecast-app/ticketing/graphql/RelayProvider';
import {AuthProvider} from './forecast-app/ticketing/features/auth/context/AuthContext';
import {EmbeddedProvider} from './forecast-app/ticketing/EmbeddedContext';
import {TicketingThemeProvider} from './forecast-app/ticketing/styles/TicketingThemeProvider';
import {SettingsRoute} from './forecast-app/ticketing/features/admin/routes/SettingsRoute';
// ABC Whyte @font-face + --fd-font-family (ACC-523: Tailwind removed).
import './forecast-app/ticketing/index.css';
import type {ticketingSettingsEmbed_Query$data} from './__generated__/ticketingSettingsEmbed_Query.graphql';

// Loaded by AdminRoutes' ForecastQueryRenderer to gate login + supply identity.
export const TicketingSettingsEmbedQuery = graphql`
	query ticketingSettingsEmbed_Query {
		viewer {
			actualPersonId
			firstName
			lastName
			fullName
			email
			permissions
		}
	}
`;

export interface TicketingSettingsEmbedProps {
	viewer: NonNullable<ticketingSettingsEmbed_Query$data['viewer']>;
}

// Local mirror of ticketing's ForecastIdentity — see ticketing-embed.tsx for why
// this wrapper keeps a local identity type rather than importing across the seam.
interface TicketingIdentity {
	id: string;
	name: string;
	email: string;
	ticketPerms: string[];
}

/**
 * Renders the ticketing settings UI inside the Forecast admin area. Mounted by
 * AdminRoutes under `/admin/tickets`, gated by `tickets_enabled` + TICKETS_ADMIN.
 */
export const TicketingSettingsEmbed: React.FC<TicketingSettingsEmbedProps> = ({viewer}) => {
	const permissions = viewer.permissions ?? [];
	const ticketPerms = permissions.filter((p): p is string => typeof p === 'string' && p.startsWith('TICKETS_'));
	const identity: TicketingIdentity = {
		id: viewer.actualPersonId ?? '',
		name: viewer.fullName ?? [viewer.firstName, viewer.lastName].filter(Boolean).join(' '),
		email: viewer.email ?? '',
		ticketPerms,
	};
	return (
		<RelayProvider>
			<AuthProvider identity={identity}>
				{/* embedded ⇒ settings tabs render in embedded mode; Forecast supplies the chrome. */}
				<EmbeddedProvider embedded={true}>
					{/* The settings tabs (and the components they pull in, e.g. the General
					    tab's RichTextEditor) read theme.ticketing.* tokens. The list/detail
					    embed gets these via App.tsx's TicketingThemeProvider, but this embed
					    mounts SettingsRoute directly, so it must supply the theme itself —
					    otherwise theme.ticketing is undefined and the admin page crashes
					    ("Cannot read properties of undefined (reading 'font')"). */}
					<TicketingThemeProvider>
						{/* The Forecast admin shell (.app) is `height: 100vh; overflow: hidden` and
						    delegates scrolling to each page. Ticketing settings brings no scroll
						    region of its own, so tall tabs (e.g. Custom Fields) overflowed the
						    clipped shell and the bottom was unreachable. This host-owned wrapper
						    scrolls internally — mirrors ticketing-embed.tsx's detail-page fix. */}
						<div style={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto'}}>
							<SettingsRoute basePath="/admin/tickets" />
						</div>
					</TicketingThemeProvider>
				</EmbeddedProvider>
			</AuthProvider>
		</RelayProvider>
	);
};

export default TicketingSettingsEmbed;
